import { useMemo, useState } from 'react';
import type { IEmployeePolicySummary, IPolicyFieldValue } from '@/@types/insurance';

interface IEmployeeDocSlots {
  identityDocFront?: unknown;
  identityDocBack?: unknown;
  drivingLicenseFront?: unknown;
  drivingLicenseBack?: unknown;
}

export function useEmployeeDetailStats(
  policies: IEmployeePolicySummary[] | undefined,
  employee: IEmployeeDocSlots | undefined
) {
  // Capture "now" once at mount so it's a stable value during render (avoids react-hooks/purity).
  const [nowMs] = useState(() => Date.now());

  const activePolicies = useMemo(
    () => (policies ?? []).filter(p => p.status === 'active').length,
    [policies]
  );

  const totalPremiumPerYear = useMemo(() => {
    const sum = (policies ?? [])
      .filter(p => p.status === 'active')
      .reduce((acc, p) => {
        const premiumField = p.fieldValues.find(
          (f: IPolicyFieldValue) =>
            f.fieldLabel.toLowerCase().includes('premium') ||
            f.fieldLabel.toLowerCase().includes('total premium')
        );
        const val = parseFloat(premiumField?.value ?? '0');
        return acc + (isNaN(val) ? 0 : val);
      }, 0);
    return sum > 0
      ? sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';
  }, [policies]);

  const expiringSoonCount = useMemo(() => {
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return (policies ?? []).filter(p => {
      if (p.status !== 'active' || !p.expiresAt) return false;
      const diff = p.expiresAt - nowMs;
      return diff > 0 && diff <= thirtyDays;
    }).length;
  }, [policies, nowMs]);

  const documentsUploaded = useMemo(() => {
    if (!employee) return 0;
    return [
      employee.identityDocFront,
      employee.identityDocBack,
      employee.drivingLicenseFront,
      employee.drivingLicenseBack,
    ].filter(d => d !== undefined && d !== null).length;
  }, [employee]);

  const documentsTotal = 4;

  return {
    activePolicies,
    totalPremiumPerYear,
    expiringSoonCount,
    documentsUploaded,
    documentsTotal,
  };
}
